'use client';

import { fetchTravelAgencyTours } from '@eliadmin/core/src/api/travel-agency/travel-agency-api';

import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../../shared/heading';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { TourCard } from '../tour-card';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { useNearViewport } from '@eliadmin/core/src/hooks/use-near-viewport';

export function TourRecommendation({ tourId }: { tourId: number }) {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const { ref, enabled } = useNearViewport();
  const { data } = useQuery({
    queryKey: ['tour-recommendations', locale, tourId],
    enabled,
    staleTime: 60_000,
    queryFn: () =>
      fetchTravelAgencyTours({
        locale,
        query: {
          page: 1,
          results: 10,
        },
      }),
  });
  const recommendedTours = (data?.data ?? []).filter(tour => tour.id !== tourId);

  if (recommendedTours.length === 0) return <div ref={ref} />;

  return (
    <div ref={ref}>
      <CarouselProvider>
        <section
          className={cn('custom-section', 'bg-[#F1F6F8] px-0 pb-16 pt-24 sm:pb-16 lg:pt-32')}
        >
          <div
            className={cn('custom-container', 'flex flex-col items-center justify-center gap-8')}
          >
            <div className="flex w-full items-center justify-start gap-6 lg:justify-between">
              <Heading title={t('text3974') ?? ''} className={{ container: 'pl-6 xl:pl-0' }} />
              <CarouselControls
                className={{
                  button:
                    'flex h-[52px] w-[52px] items-center justify-center rounded-[16px] border-none bg-white text-xl text-primary shadow-none hover:bg-white/90 hover:text-primary',
                  main: 'hidden items-center justify-end gap-2 lg:flex',
                }}
              />
            </div>
            <Carousel
              className={{
                container: 'w-full gap-4 sm:gap-6 lg:gap-8',
                wrapper: 'w-full',
                main: 'w-full',
                slide:
                  'w-[86%] first:pl-6 last:pr-6 sm:w-full sm:max-w-[405px] first:xl:pl-0 last:xl:pr-0',
              }}
              slides={recommendedTours.map(tour => (
                <TourCard key={tour.id} tour={tour as any} />
              ))}
            />
          </div>
        </section>
      </CarouselProvider>
    </div>
  );
}
